/**
 * "Report this design" — a sentence from the customer, and the design that prompted it.
 *
 * A button that opens into a small form, in place, rather than a dialog: the shed the
 * report is about stays on screen beside it, which is the thing the customer is
 * describing. The form says exactly what will be sent with the note — the design, the
 * engine, the spec version and the build — because a customer deciding whether to
 * press *send* deserves to know, and because "the design" is a phrase that could mean
 * a screenshot or an account and means neither.
 *
 * It asks for no name and no address, and says so: we read reports and do not answer
 * them, and a form with an email field in it is a promise of a reply. If the customer
 * types an address into the note anyway, the edge takes it out before storing it
 * (DESIGN §9) and this form tells them that happened, so nobody waits on an answer
 * that cannot come.
 *
 * When the edge cannot be reached the design goes on the clipboard instead, which is
 * the one part of the old `mailto` path worth keeping.
 */

import { SPEC_VERSION, toFeetInches, type Spec } from "@planforge/wasm";
import { useCallback, useEffect, useState, type ReactElement } from "react";

import {
  MAX_NOTE_CHARS,
  REPORT_UNREACHABLE,
  buildId,
  copySpec,
  engineVersion,
  reportRequest,
  sendReport,
  type ReportContext,
  type ReportResponse,
} from "../feedback/report.js";
import { plainly } from "../purchase/api.js";

type State =
  | { step: "closed" }
  | { step: "writing" }
  | { step: "sending" }
  | { step: "sent"; receipt: ReportResponse }
  | { step: "failed"; problem: string; copied?: boolean };

/** A one-line description of the design, so "the design" names something. */
export function describeDesign(spec: Spec): string {
  const openings = spec.openings.length;
  return (
    `${toFeetInches(spec.footprint.width)} × ${toFeetInches(spec.footprint.length)}, ` +
    `${openings} ${openings === 1 ? "opening" : "openings"}`
  );
}

export function ReportDesign({
  spec,
  context = "configurator",
  startOpen = false,
  className = "button ghost",
}: {
  spec: Spec;
  /** Where the form is: the panel, or the crash screen. Sent with the report. */
  context?: ReportContext;
  /** The crash screen opens straight into the form: reporting is why it is there. */
  startOpen?: boolean;
  className?: string;
}): ReactElement {
  const [state, setState] = useState<State>(
    startOpen ? { step: "writing" } : { step: "closed" },
  );
  const [note, setNote] = useState("");
  const [engine, setEngine] = useState<string | undefined>(undefined);

  // Asked for when the form opens rather than when it is sent, so the line saying what
  // goes with the report names the real engine before the customer presses send.
  const open = state.step !== "closed";
  useEffect(() => {
    if (!open || engine !== undefined) {
      return;
    }
    let live = true;
    void engineVersion().then((version) => {
      if (live) {
        setEngine(version);
      }
    });
    return () => {
      live = false;
    };
  }, [open, engine]);

  const send = useCallback(async () => {
    setState({ step: "sending" });
    try {
      const receipt = await sendReport(
        reportRequest({
          spec,
          note,
          context,
          engineVersion: engine ?? (await engineVersion()),
          buildId: buildId(),
        }),
      );
      setState({ step: "sent", receipt });
      setNote("");
    } catch (reason) {
      setState({
        step: "failed",
        problem: plainly(reason, REPORT_UNREACHABLE),
      });
    }
  }, [spec, note, context, engine]);

  if (state.step === "closed") {
    return (
      <button type="button" className={className} onClick={() => setState({ step: "writing" })}>
        Report this design
      </button>
    );
  }

  if (state.step === "sent") {
    return (
      <div className="report" role="status">
        <p className="report-title">Thanks — your report is in.</p>
        <p>
          It is filed as <code className="report-reference">{state.receipt.reference}</code>{" "}
          with this design attached, so we can rebuild exactly what you were looking at.
        </p>
        {state.receipt.addressesRemoved > 0 ? (
          <p className="report-note">
            We took the email address out of your note before keeping it. We don&rsquo;t
            store addresses, so we couldn&rsquo;t have replied to it.
          </p>
        ) : null}
        <div className="report-actions">
          <button type="button" className="button" onClick={() => setState({ step: "closed" })}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const sending = state.step === "sending";
  return (
    <form
      className="report"
      aria-label="Report this design"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <label className="report-title" htmlFor="report-note">
        What looks wrong, or what were you trying to do?
      </label>
      <textarea
        id="report-note"
        className="report-text"
        rows={4}
        maxLength={MAX_NOTE_CHARS}
        value={note}
        disabled={sending}
        placeholder="The left wall's window sits lower than I set it…"
        onChange={(event) => setNote(event.target.value)}
      />
      <p className="report-attached">
        Sent with it: this design ({describeDesign(spec)}), engine{" "}
        {engine ?? "…"}, spec version {SPEC_VERSION}, build {buildId()}.
      </p>
      <p className="report-note">
        No name or email needed — we read every report, but we can&rsquo;t reply to one.
      </p>
      {state.step === "failed" ? (
        <p className="report-problem" role="alert">
          {state.problem}{" "}
          <button
            type="button"
            className="link"
            onClick={() =>
              void copySpec(spec).then((copied) => setState({ ...state, copied }))
            }
          >
            {state.copied === true ? "Design copied ✓" : "Copy the design instead"}
          </button>
        </p>
      ) : null}
      <div className="report-actions">
        <button type="submit" className="button send" disabled={sending}>
          {sending ? "Sending…" : state.step === "failed" ? "Try again" : "Send report"}
        </button>
        <button
          type="button"
          className="button ghost"
          disabled={sending}
          onClick={() => setState({ step: "closed" })}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
